import { BaseModel } from '@cotera/client/v0/actions/framework/model.base';
import type { ApplicableContext } from '@cotera/client/v0/actions/framework';
import type { ResourceNavigator } from './resource-navigator';

/**
 * The actions-side handle on {@link ResourceNavigator}. Actions can't read React
 * context, so the app shell owns one of these and artifact panels push their
 * navigator onto it while mounted.
 *
 * A LIFO stack rather than a single slot, and a single root-owned model rather
 * than one registered per panel, because the model scope makes both alternatives
 * wrong: `addModel` de-dupes on `type:id` and returns the *existing* instance
 * (so a second panel's model is silently dropped and disposed), while distinct
 * ids fall to `getModelOfType`'s first-added-wins ordering (so the stalest
 * navigator would win). With a stack, nesting resolves to the innermost surface
 * and overlapping mount/unmount during a route change pops by identity, so order
 * doesn't matter.
 */
export class ResourceNavigatorModel extends BaseModel {
  private readonly stack: ResourceNavigator[] = [];

  constructor(private readonly base: ResourceNavigator) {
    super('resource-navigator');
  }

  /** The innermost mounted surface, or the app-level page navigator. */
  get current(): ResourceNavigator {
    return this.stack[this.stack.length - 1] ?? this.base;
  }

  /**
   * Make `navigator` current until the returned pop is called. The pop removes
   * by identity and is idempotent, so it's safe as an effect cleanup under
   * StrictMode's double-invoke and during overlapping route transitions.
   */
  push(navigator: ResourceNavigator): () => void {
    this.stack.push(navigator);
    let popped = false;
    return () => {
      if (popped) {
        return;
      }
      popped = true;
      const index = this.stack.lastIndexOf(navigator);
      if (index >= 0) {
        this.stack.splice(index, 1);
      }
    };
  }
}

/**
 * The navigator an action should use. Falls back to the injected one when no
 * model is in scope, which is mainly tests and subtrees mounted outside the
 * app shell.
 */
export const resolveResourceNavigator = (
  context: Pick<ApplicableContext, 'getInScopeModelOfType'>,
  fallback: ResourceNavigator
): ResourceNavigator =>
  context.getInScopeModelOfType(ResourceNavigatorModel)?.current ?? fallback;
